import type { AvailabilityStatus } from '../types';
import type { PlanningRecommendation } from './planningRecommendations';

export type BackToBackTolerance = 'no' | 'if_needed' | 'yes';
export type TeamPriority = 'primary' | 'normal' | 'secondary';
export type OpportunityPressure = 'normal' | 'due_soon' | 'critical' | 'at_risk' | 'unreachable';

export type PlanningIntelligenceTeam = {
  id: string;
  seasonId: string;
  teamName: string;
  enforceMaxSit: boolean;
  maxWeeksWithoutMatch: number;
};

export type PlanningIntelligenceRoster = {
  teamId: string;
  playerId: string;
  targetMatchCount: number | null;
  teamPriority?: TeamPriority | null;
};

export type PlanningIntelligenceMatch = {
  id: string;
  teamId: string;
  seasonId: string;
  matchDate: string;
  matchStatus: string;
  lineupStatus: string | null;
  opponentName?: string | null;
};

export type PlanningIntelligenceAssignment = {
  matchId: string;
  playerId: string;
  positionCode?: string | null;
};

export type PlanningIntelligenceAvailability = {
  matchId: string;
  playerId: string;
  status: AvailabilityStatus;
};

export type PlayerPlanningInsight = {
  playerId: string;
  teamId: string;
  seasonId: string;
  targetMatches: number;
  completedMatches: number;
  committedFutureAssignments: number;
  draftFutureAssignments: number;
  matchesNeeded: number;
  availableOpportunities: number;
  maybeOpportunities: number;
  availableOpportunityMargin: number;
  pressure: OpportunityPressure;
  availableButNotSelected: number;
  lastMatchAnywhere: string | null;
  lastTeamMatch: string | null;
  daysSinceAnyMatch: number | null;
  daysSinceTeamMatch: number | null;
  maxWeeksWithoutMatch: number;
  enforceMaxSit: boolean;
  teamPriority: TeamPriority;
  backToBackTolerance: BackToBackTolerance;
  sameDayTolerance: BackToBackTolerance;
  hasBackToBack: boolean;
  hasSameDayLoad: boolean;
  hasThreeInThree: boolean;
  contingentAssignmentMatchIds: string[];
  futureOpportunities: Array<{
    matchId: string;
    matchDate: string;
    teamId: string;
    teamName: string;
    opponentName: string | null;
    availability: AvailabilityStatus;
    assignmentState: 'none' | 'draft' | 'committed';
    positionCode: string | null;
  }>;
};

export type PlanningIntelligenceInput = {
  today: string;
  teams: PlanningIntelligenceTeam[];
  rosters: PlanningIntelligenceRoster[];
  matches: PlanningIntelligenceMatch[];
  assignments: PlanningIntelligenceAssignment[];
  availability: PlanningIntelligenceAvailability[];
  playerNames: Map<string, string>;
  playerTolerances?: Map<string, BackToBackTolerance | null | undefined>;
  playerSameDayTolerances?: Map<string, BackToBackTolerance | null | undefined>;
  completedParticipation?: Set<string>;
};

const dayMs = 24 * 60 * 60 * 1000;

function dayDifference(later: string, earlier: string): number {
  return Math.max(0, Math.round((Date.parse(`${later}T12:00:00Z`) - Date.parse(`${earlier}T12:00:00Z`)) / dayMs));
}

function shiftIsoDate(value: string, days: number): string {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function assignmentState(match: PlanningIntelligenceMatch): 'completed' | 'committed' | 'draft' | 'none' {
  if (match.matchStatus === 'Completed') return 'completed';
  if (match.lineupStatus === 'Published') return 'committed';
  if (match.lineupStatus === 'Draft') return 'draft';
  return 'none';
}

function normalizeTolerance(value: BackToBackTolerance | null | undefined): BackToBackTolerance {
  return value === 'no' || value === 'yes' ? value : 'if_needed';
}

function normalizePriority(value: TeamPriority | null | undefined): TeamPriority {
  return value === 'primary' || value === 'secondary' ? value : 'normal';
}

function formatMatchDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function pressureFor(matchesNeeded: number, available: number, maybe: number): OpportunityPressure {
  if (matchesNeeded <= 0) return 'normal';
  if (available + maybe < matchesNeeded) return 'unreachable';
  if (available < matchesNeeded) return 'at_risk';
  if (available === matchesNeeded) return 'critical';
  if (available - matchesNeeded <= 1) return 'due_soon';
  return 'normal';
}

function recommendation(
  type: PlanningRecommendation['recommendationType'],
  insight: PlayerPlanningInsight,
  message: string,
  priority: PlanningRecommendation['priority'],
  impact: number,
  signature: string,
): PlanningRecommendation {
  const baseKey = `planning:${type}:${insight.teamId}:${insight.seasonId}:${insight.playerId}`;
  return {
    key: `${baseKey}:state:${encodeURIComponent(signature)}`,
    baseKey,
    recommendationType: type,
    teamId: insight.teamId,
    seasonId: insight.seasonId,
    playerId: insight.playerId,
    stateSignature: signature,
    message,
    priority,
    impact,
  };
}

export function buildPlanningIntelligence(input: PlanningIntelligenceInput): {
  insights: PlayerPlanningInsight[];
  recommendations: PlanningRecommendation[];
} {
  const teamById = new Map(input.teams.map((team) => [team.id, team]));
  const matchById = new Map(input.matches.map((match) => [match.id, match]));
  const assignmentsByPlayer = new Map<string, PlanningIntelligenceAssignment[]>();
  input.assignments.forEach((assignment) => assignmentsByPlayer.set(assignment.playerId, [...(assignmentsByPlayer.get(assignment.playerId) || []), assignment]));
  const availabilityByCell = new Map(input.availability.map((row) => [`${row.matchId}:${row.playerId}`, row.status]));
  const activeMatches = input.matches.filter((match) => match.matchStatus !== 'Cancelled');

  const insightFor = (roster: PlanningIntelligenceRoster): PlayerPlanningInsight => {
    const team = teamById.get(roster.teamId);
    const playerAssignments = (assignmentsByPlayer.get(roster.playerId) || [])
      .map((assignment) => ({ assignment, match: matchById.get(assignment.matchId) }))
      .filter((row): row is { assignment: PlanningIntelligenceAssignment; match: PlanningIntelligenceMatch } => Boolean(row.match && row.match.matchStatus !== 'Cancelled'));
    // Targets and participation fairness are Player × Team × Season. A player
    // may remain on the same team across seasons, but an earlier season must
    // never satisfy this season's target or reduce its opportunity pressure.
    const isCurrentTeamSeason = (match: PlanningIntelligenceMatch) => (
      match.teamId === roster.teamId && match.seasonId === team?.seasonId
    );
    const teamPlayerAssignments = playerAssignments.filter(({ match }) => isCurrentTeamSeason(match));
    const completedAssignments = playerAssignments.filter(({ assignment, match }) => (
      match.matchStatus === 'Completed' && (!input.completedParticipation || input.completedParticipation.has(`${assignment.matchId}:${roster.playerId}`))
    ));
    const teamCompletedAssignments = completedAssignments.filter(({ match }) => isCurrentTeamSeason(match));
    const committedAssignments = teamPlayerAssignments.filter(({ match }) => match.matchDate >= input.today && assignmentState(match) === 'committed');
    const draftAssignments = teamPlayerAssignments.filter(({ match }) => match.matchDate >= input.today && assignmentState(match) === 'draft');
    const teamMatches = activeMatches.filter(isCurrentTeamSeason);
    const futureTeamMatches = teamMatches.filter((match) => match.matchDate >= input.today && match.matchStatus !== 'Completed');
    const targetMatches = Math.max(0, roster.targetMatchCount || 0);
    const matchesNeeded = Math.max(0, targetMatches - teamCompletedAssignments.length - committedAssignments.length);
    const opportunities = futureTeamMatches.map((match) => {
      const assigned = playerAssignments.find((row) => row.assignment.matchId === match.id);
      const state = assigned ? assignmentState(match) : 'none';
      return {
        matchId: match.id,
        matchDate: match.matchDate,
        teamId: match.teamId,
        teamName: team?.teamName || 'Team',
        opponentName: match.opponentName || null,
        availability: availabilityByCell.get(`${match.id}:${roster.playerId}`) || 'unknown',
        assignmentState: state === 'completed' ? 'none' : state,
        positionCode: assigned?.assignment.positionCode || null,
      } as PlayerPlanningInsight['futureOpportunities'][number];
    });
    const availableOpportunities = opportunities.filter((row) => row.availability === 'available' && row.assignmentState !== 'committed').length;
    const maybeOpportunities = opportunities.filter((row) => row.availability === 'maybe' && row.assignmentState !== 'committed').length;
    const completedDates = completedAssignments.map(({ match }) => match.matchDate).sort();
    const teamCompletedDates = completedAssignments.filter(({ match }) => isCurrentTeamSeason(match)).map(({ match }) => match.matchDate).sort();
    const availableButNotSelected = teamMatches.filter((match) => match.matchDate < input.today && availabilityByCell.get(`${match.id}:${roster.playerId}`) === 'available' && !playerAssignments.some(({ assignment }) => assignment.matchId === match.id)).length;
    const allDatedAssignments = playerAssignments.map(({ match }) => match).sort((left, right) => left.matchDate.localeCompare(right.matchDate));
    // Include the immediately preceding completed match so a captain planning
    // today can see a Friday/Saturday or Saturday/Sunday workload, without
    // surfacing stale historical clusters as current scheduling warnings.
    const workloadAssignments = allDatedAssignments.filter((match) => match.matchDate >= shiftIsoDate(input.today, -1) && assignmentState(match) !== 'none');
    const workloadDates = workloadAssignments.map((match) => match.matchDate);
    const hasSameDayLoad = workloadDates.some((date) => workloadDates.filter((item) => item === date).length > 1);
    const hasBackToBack = workloadDates.some((date, index) => workloadDates.some((other, otherIndex) => index !== otherIndex && dayDifference(date, other) === 1));
    const hasThreeInThree = workloadDates.some((date) => workloadDates.filter((other) => Math.abs(dayDifference(date, other)) <= 2).length >= 3);
    const tolerance = normalizeTolerance(input.playerTolerances?.get(roster.playerId));
    const sameDayTolerance = normalizeTolerance(input.playerSameDayTolerances?.get(roster.playerId));
    return {
      playerId: roster.playerId,
      teamId: roster.teamId,
      seasonId: team?.seasonId || '',
      targetMatches,
      completedMatches: teamCompletedAssignments.length,
      committedFutureAssignments: committedAssignments.length,
      draftFutureAssignments: draftAssignments.length,
      matchesNeeded,
      availableOpportunities,
      maybeOpportunities,
      availableOpportunityMargin: availableOpportunities - matchesNeeded,
      pressure: pressureFor(matchesNeeded, availableOpportunities, maybeOpportunities),
      availableButNotSelected,
      lastMatchAnywhere: completedDates.at(-1) || null,
      lastTeamMatch: teamCompletedDates.at(-1) || null,
      daysSinceAnyMatch: completedDates.at(-1) ? dayDifference(input.today, completedDates.at(-1)!) : null,
      daysSinceTeamMatch: teamCompletedDates.at(-1) ? dayDifference(input.today, teamCompletedDates.at(-1)!) : null,
      maxWeeksWithoutMatch: Math.max(1, team?.maxWeeksWithoutMatch || 2),
      enforceMaxSit: team?.enforceMaxSit === true,
      teamPriority: normalizePriority(roster.teamPriority),
      backToBackTolerance: tolerance,
      sameDayTolerance,
      hasBackToBack,
      hasSameDayLoad,
      hasThreeInThree,
      contingentAssignmentMatchIds: opportunities.filter((row) => row.assignmentState !== 'none' && row.availability === 'maybe').map((row) => row.matchId),
      futureOpportunities: opportunities,
    };
  };

  const insights = input.rosters.filter((roster) => teamById.has(roster.teamId)).map(insightFor);
  const recommendations = insights.flatMap((insight) => {
    const name = input.playerNames.get(insight.playerId) || 'This player';
    const rows: PlanningRecommendation[] = [];
    if (insight.pressure === 'unreachable') rows.push(recommendation('target_unreachable', insight, `${name} needs ${insight.matchesNeeded} more match${insight.matchesNeeded === 1 ? '' : 'es'} but has only ${insight.availableOpportunities} Available and ${insight.maybeOpportunities} Maybe opportunities remaining.`, 'high', 100, `need=${insight.matchesNeeded};available=${insight.availableOpportunities};maybe=${insight.maybeOpportunities}`));
    else if (insight.pressure === 'at_risk') rows.push(recommendation('target_at_risk', insight, `${name} needs ${insight.matchesNeeded} more match${insight.matchesNeeded === 1 ? '' : 'es'}; Available opportunities are insufficient, so a Maybe response may be needed.`, 'high', 85, `need=${insight.matchesNeeded};available=${insight.availableOpportunities};maybe=${insight.maybeOpportunities}`));
    else if (insight.pressure === 'critical' || insight.pressure === 'due_soon') rows.push(recommendation('scarce_opportunity', insight, `${name} needs ${insight.matchesNeeded} more match${insight.matchesNeeded === 1 ? '' : 'es'} and has only ${insight.availableOpportunities} Available opportunit${insight.availableOpportunities === 1 ? 'y' : 'ies'} remaining.`, insight.pressure === 'critical' ? 'high' : 'medium', insight.pressure === 'critical' ? 75 : 55, `need=${insight.matchesNeeded};available=${insight.availableOpportunities}`));
    const thresholdDays = insight.maxWeeksWithoutMatch * 7;
    if (insight.enforceMaxSit && insight.daysSinceTeamMatch != null && insight.daysSinceTeamMatch >= Math.max(0, thresholdDays - 3)) rows.push(recommendation('participation_gap', insight, `${name} last played for this team ${insight.daysSinceTeamMatch} days ago; the team's ${insight.maxWeeksWithoutMatch}-week participation threshold is approaching.`, insight.daysSinceTeamMatch >= thresholdDays ? 'high' : 'medium', insight.daysSinceTeamMatch, `days=${insight.daysSinceTeamMatch};threshold=${thresholdDays}`));
    if (insight.availableButNotSelected >= 2) rows.push(recommendation('available_not_selected', insight, `${name} has been Available but not selected for ${insight.availableButNotSelected} earlier match${insight.availableButNotSelected === 1 ? '' : 'es'}.`, 'medium', insight.availableButNotSelected, `count=${insight.availableButNotSelected}`));
    if (insight.contingentAssignmentMatchIds.length > 0) {
      const nextContingent = insight.futureOpportunities
        .filter((opportunity) => opportunity.assignmentState !== 'none' && opportunity.availability === 'maybe')
        .sort((left, right) => left.matchDate.localeCompare(right.matchDate))[0];
      const daysAway = nextContingent ? dayDifference(nextContingent.matchDate, input.today) : Number.POSITIVE_INFINITY;
      const line = nextContingent?.positionCode ? ` for ${nextContingent.positionCode}` : '';
      rows.push(recommendation(
        'maybe_assignment',
        insight,
        nextContingent
          ? `Confirm ${name}${line} on ${formatMatchDate(nextContingent.matchDate)}. ${name} is assigned but still marked Maybe.`
          : `${name} is assigned while marked Maybe. Confirm availability before the match.`,
        daysAway <= 3 ? 'high' : 'medium',
        daysAway <= 3 ? 90 : 60,
        insight.contingentAssignmentMatchIds.join(','),
      ));
    }
    if ((insight.hasSameDayLoad && insight.sameDayTolerance !== 'yes') || insight.hasThreeInThree || (insight.hasBackToBack && insight.backToBackTolerance !== 'yes')) {
      const priorityContext = insight.teamPriority === 'normal' ? '' : ` This team is ${insight.teamPriority} priority.`;
      const toleranceLabel = insight.hasSameDayLoad ? insight.sameDayTolerance : insight.backToBackTolerance;
      rows.push(recommendation('workload', insight, `${name} has ${insight.hasSameDayLoad ? 'multiple same-day matches' : insight.hasThreeInThree ? 'three matches within three days' : 'back-to-back match days'}; scheduling tolerance is ${toleranceLabel.replace(/_/g, ' ')}.${priorityContext}`, insight.hasSameDayLoad || insight.hasThreeInThree || toleranceLabel === 'no' ? 'high' : 'medium', insight.hasSameDayLoad ? 90 : insight.hasThreeInThree ? 80 : 55, `${insight.hasSameDayLoad}:${insight.hasThreeInThree}:${insight.hasBackToBack}:${toleranceLabel}:${insight.teamPriority}`));
    }
    return rows;
  });
  return { insights, recommendations };
}
